// BRK-S105 — the write-only SSN / driver's licence indicator (decision D-11).
//
// Plaintext SSN and driver's licence NEVER reach this browser, and nothing here
// changes that. PMS-S237 serves an INDICATOR instead — ciphertext PRESENCE plus
// the SSN's last four from a stored plaintext column — on every person in an
// account read, and REDACTS the two sensitive paths out of every bond-order bag
// it echoes back. So the bond flow can tell a broker "we already have this"
// without the portal ever holding the value.
//
// The server half is the other side of the same decision: a bond step save that
// edits the indemnitor and leaves the SSN blank has the stored plaintext
// injected into the DISCOVERY-BOUND payload only (PMS `withStoredPersonPii`,
// lib/broker-bond.ts). That injection is what makes waiving the required gate
// honest — the stored value really does reach the bureau — and it is also why
// a blank is sent as an ABSENT KEY rather than as an empty string: PMS treats
// absent, empty, and a masked rendering identically, and an absent key is the
// only one of the three that does not claim the broker typed something.
//
// WHICH PERSON. PMS injects ONE person's stored PII, and since PMS-S238 it says
// which: `pii.usedForWrite` on the indicator itself. The indicator this portal
// renders must describe that same person, or the flow would promise a value the
// server would not use — so `primaryOwner` below READS the flag. It used to
// re-derive it from `ownershipPercentage`; see the note there for why that was
// never sound, only usually right.
//
// ⚠️ THAT IS THE RULE FOR A STEP WITH ONE BLOCK. A ROSTER step draws a block
// per person, and each of them has their own answer: the account read carries
// `pii` on EVERY person, keyed by the personnel id the roster addresses them
// with. So `withRosterPiiOnFile` waives PER PERSON (BRK-S165), and
// `usedForWrite` keeps its own narrower job — it names whose stored value a
// write injects, which is a question about the WRITE and not about the form.

import type { Personnel, PersonnelPii } from "@/lib/brokerAccounts";
import type { BondRenderField, BondValueBag } from "@/lib/bondFields";
import { contractPathOf, unscopeRosterName } from "@/lib/bondRoster";

/** The bond-order value path holding an indemnitor's SSN. */
export const BOND_SSN_PATH = "personal[].ssn";
/** The bond-order value path holding an indemnitor's driver's licence number. */
export const BOND_DL_PATH = "personal[].driversLicenseNumber";

/**
 * The two write-only PII paths, mirrored from PMS's `BOND_PII_PATHS`. This list
 * is the contract: PMS injects for exactly these, and its bond view redacts
 * exactly these. Adding a third path here without the server half would waive a
 * required gate for a value that then reaches nobody.
 */
export const BOND_PII_PATHS = [BOND_SSN_PATH, BOND_DL_PATH] as const;

/**
 * Whether a path is one of the two, in EITHER spelling.
 *
 * The roster's form bag scopes a field name to the PERSON it is about
 * (`<ownerId>::personal[].ssn`, BRK-S152), so a comparison against the bare
 * contract path stopped matching the moment the indemnitor step became a
 * roster — and the failure is the quiet one this whole module exists to avoid:
 * a blank SSN would go on the wire as `""`, claiming the broker cleared a field
 * PMS holds.
 */
export function isBondPiiPath(name: string): boolean {
  return (BOND_PII_PATHS as readonly string[]).includes(contractPathOf(name));
}

/**
 * Blank in the D-8 sense — "use the stored value" — mirroring PMS's
 * `isBlankPiiValue` verbatim: absent, null, empty/whitespace, or a MASKED
 * rendering. The masked case exists because a UI that pre-filled the mask into
 * the input would otherwise send bullets as if they were digits; this portal
 * never pre-fills, but the rule is mirrored so the guard can pin it and so a
 * pasted mask behaves the way the server says it does.
 */
export function isBlankPiiValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && (value.trim() === "" || /[•*●]/.test(value)))
  );
}

/**
 * The person PMS will inject stored PII for — **as the server states it**, not
 * as this portal works it out (PMS-S238, adopted in BRK-S110).
 *
 * `pii.usedForWrite` is published per person on every account read: true for at
 * most one, and for NOBODY when no personnel holds an ownership share. So this
 * is a `find`, and a `null` here means the server would inject for nobody —
 * the same fact a step save reports as `NO_OWNER_ON_ACCOUNT`.
 *
 * IT USED TO BE A RANKING. This function re-derived the answer by taking the
 * largest `ownershipPercentage`, with a first-wins tie-break mirroring the
 * server's `>` comparison. That is exactly the inference the flag was published
 * to retire, and its failure case is not exotic: two 50/50 owners have no
 * largest shareholder at all, so the answer rested ENTIRELY on which of two
 * equal people the mirrored tie-break happened to reach first. Two
 * implementations agreeing about a tie by construction is not agreement; it is
 * two coin flips that have so far landed the same way. Re-deriving it also had
 * to keep pace with a rule owned two services upstream, which it could only
 * ever do late.
 *
 * DO NOT reintroduce a positional or share-based fallback for the `null` case.
 * "Nobody" is an answer the flow can render honestly; guessing produces a
 * promise the server will not keep — an indicator claiming a stored SSN will be
 * used for a person the write never reads from.
 */
export function primaryOwner(personnel: Personnel[]): Personnel | null {
  // `pii?.` rather than `pii.` — NOT tolerance for an older shape, but the
  // difference between degrading and white-screening (NN #7). The contract marks
  // `pii` required and the guard pins that, so a person without it is a
  // MALFORMED payload; reading a property OF a missing object throws, where the
  // share this function used to read would merely have been undefined. A
  // TypeError here lands inside a render and takes the whole bond flow down, so
  // a malformed person is skipped and the flow reports "nobody" — the same
  // handled state a genuinely ownerless account produces.
  return personnel.find((person) => person.pii?.usedForWrite) ?? null;
}

/** What the indicator says about one field: on file, and (SSN only) its last
 *  four digits. `null` for a path that is not one of the two PII paths. */
export interface PiiOnFile {
  onFile: boolean;
  /** Always null for the driver's licence — PMS stores no last four for it. */
  last4: string | null;
}

export function onFileFor(
  rawPath: string,
  pii: PersonnelPii | null | undefined,
): PiiOnFile | null {
  if (!pii) return null;
  // Either spelling — see `isBondPiiPath`.
  const path = contractPathOf(rawPath);
  if (path === BOND_SSN_PATH) {
    return { onFile: pii.ssn.onFile, last4: pii.ssn.last4 };
  }
  if (path === BOND_DL_PATH) {
    return { onFile: pii.driversLicense.onFile, last4: null };
  }
  return null;
}

/** The masked rendering of a stored SSN. Last four alone identifies nobody,
 *  which is the whole reason PMS is willing to serve it. */
export function maskedSsn(last4: string): string {
  return `•••-••-${last4}`;
}

/**
 * The line shown under an on-file field. Two renderings, never three: with the
 * stored last four, and without.
 *
 * A row encrypted before PMS-S237 added the `ssn_last4` column has `last4:
 * null` — on file, last four unknown. That renders with NO digits rather than
 * with placeholder ones: fabricated digits would read as a value the broker
 * could check against their own records, and they would be wrong.
 */
export function onFileNotice(label: string, last4: string | null): string {
  const masked = last4 ? ` (${maskedSsn(last4)})` : "";
  return `${label} on file${masked} — leave blank to use it, or enter a new value`;
}

/**
 * The step's render fields with the on-file waiver applied: a PII field whose
 * value PMS already holds becomes OPTIONAL and carries its indicator line.
 *
 * Applied once, to the fields both the renderer and the validators read, so the
 * control, the required checklist, and the resume-position rule cannot disagree
 * about whether the field still owes an answer.
 */
export function withPiiOnFile(
  fields: BondRenderField[],
  pii: PersonnelPii | null | undefined,
): BondRenderField[] {
  return fields.map((field) => {
    const stored = onFileFor(field.name, pii);
    if (!stored?.onFile) return field;
    return {
      ...field,
      required: false,
      onFileNotice: onFileNotice(field.label, stored.last4),
    };
  });
}

/**
 * The bag as it goes ON THE WIRE: a blank PII path is OMITTED, not sent as "".
 *
 * Both spellings trigger PMS's injection, so this is not the difference between
 * working and broken — it is the difference between saying nothing and claiming
 * the broker cleared the field. `undefined` members are dropped for every path
 * for the same reason (and because JSON would drop them anyway, which is worth
 * making explicit where a test can assert it).
 */
export function omitBlankPii(values: BondValueBag): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(values)) {
    if (value === undefined) continue;
    if (isBondPiiPath(name) && isBlankPiiValue(value)) continue;
    out[name] = value;
  }
  return out;
}

/**
 * The on-file indicator for EVERY person on an account, keyed by the id a bond
 * roster addresses them with (BRK-S165).
 *
 * A roster's `ownerId` **is** PMS's own personnel id for anybody the account
 * holds, so the account read is already keyed the way the roster asks — no
 * matching, no names, no ranking. A client-minted `brk-…` id belongs to
 * somebody PMS has never heard of, so it simply misses this map, which is the
 * honest answer: a person who does not exist yet has nothing on file.
 *
 * `pii` is required by the contract, so a person without it is a MALFORMED
 * payload rather than an older shape — skipped for the reason `primaryOwner`
 * reads it defensively: a TypeError inside a render takes the whole bond flow
 * down, and the handled outcome (no indicator) is the one that costs the broker
 * a retype rather than the page (NN #7).
 */
export function rosterPiiByOwnerId(
  personnel: readonly Personnel[],
): Record<string, PersonnelPii> {
  const out: Record<string, PersonnelPii> = {};
  for (const person of personnel) {
    if (person?.pii && typeof person.id === "string")
      out[person.id] = person.pii;
  }
  return out;
}

/** Whose indicator to apply to a scoped field — the account's people by roster
 *  identity (`rosterPiiByOwnerId`). Undefined for a person PMS holds nothing
 *  for, which is every person it has never heard of. */
export type RosterPiiLookup = (
  ownerId: string,
) => PersonnelPii | null | undefined;

/**
 * The waiver on a ROSTER step — applied PER PERSON (BRK-S165).
 *
 * ⚠️ IT USED TO REACH ONE BLOCK. BRK-S152 waived the SSN and licence on the
 * first owner's block alone and left everybody else required, because PMS's
 * `pii.usedForWrite` names at most one person per account and the roster was
 * new. That was never the whole indicator: the account read publishes
 * `personnel[].pii` on EVERY person, so "is there an SSN on file for THIS
 * person" has always been answerable for each of them — `usedForWrite` answers
 * a different question (whose stored value a write injects), and using it as
 * the waiver's key made every other owner retype an SSN PMS already held, on
 * every resume, for as long as the order existed.
 *
 * The rule is unchanged in the direction that matters: a field is waived only
 * where the SERVER says that person's value is on file, so the waiver never
 * promises a stored value for somebody who has none. An UNSCOPED field is left
 * alone — it belongs to the order rather than to a person, and there is nobody
 * to look up.
 */
export function withRosterPiiOnFile(
  fields: BondRenderField[],
  piiFor: RosterPiiLookup,
  session?: RosterSsnSession,
): BondRenderField[] {
  return fields.map((field) => {
    const scoped = unscopeRosterName(field.name);
    if (scoped === null) return field;
    const pii = piiFor(scoped.ownerId);
    const waived = withPiiOnFile([field], pii)[0];
    if (!session || contractPathOf(field.name) !== BOND_SSN_PATH) {
      return waived;
    }
    return withStoredSsn(field, waived, pii, session);
  });
}

// ── BRK-S316: the SSN is MASKED once it is on file ───────────────────────────
//
// Daniel's walk (week of 10/4): going back to the indemnitor step showed an
// EMPTY SSN box beside a line of small print, which reads as "we lost it" — and
// invites retyping, or a save the broker fears will wipe it. So an SSN that is
// on file is now drawn as `•••-••-1234` with a Change affordance, and an eye
// that reveals the full value ONLY when this session still holds what the
// broker typed. The plaintext still never comes back from PMS; the reveal is
// the broker's own keystrokes, kept in page memory (NN #9 — gone on reload).
//
// The wire is unchanged and that is the point: an untouched masked field has
// no value in the bag, so `omitBlankPii` sends NO `ssn` entry for that person
// and PMS preserves what it holds (the PMS-S184 preservation shape).

/** What this session knows about the SSN fields beyond the server's
 *  indicator: what a save accepted (memory only), and which ones the broker
 *  has asked to replace. Both keyed by the SCOPED field name. */
export interface RosterSsnSession {
  held: (name: string) => string | undefined;
  changing: (name: string) => boolean;
}

function withStoredSsn(
  field: BondRenderField,
  waived: BondRenderField,
  pii: PersonnelPii | null | undefined,
  session: RosterSsnSession,
): BondRenderField {
  const held = session.held(field.name);
  // `?.` on `ssn` for the reason `primaryOwner` reads defensively: a malformed
  // indicator degrades to "not on file", never to a TypeError in a render.
  const indicator = pii?.ssn;
  // A value THIS SESSION saved is on file even before the account re-read
  // confirms it: the save's 2xx is the server accepting it, and the gap until
  // the re-read lands is exactly when the box used to come back empty.
  if (!indicator?.onFile && held === undefined) return waived;
  // The held value is the newer fact — the broker typed it after the read the
  // indicator came from — so its last four win.
  const last4 = held ? digitsOf(held).slice(-4) : (indicator?.last4 ?? null);
  const storedSsn = { last4 };
  // Change puts the gate back: the broker said they are replacing the stored
  // value, so a blank is no longer "keep it" (see `clearedStoredSsn`).
  if (session.changing(field.name)) return { ...field, storedSsn };
  return { ...waived, required: false, storedSsn };
}

function digitsOf(value: string): string {
  return value.replace(/\D/g, "");
}

/** The full SSN as it is revealed — formatted from its digits, whatever the
 *  broker typed between them. Only ever called on a value this session holds. */
export function formatSsn(value: string): string {
  const d = digitsOf(value);
  return d.length === 9
    ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
    : value;
}

/**
 * Stored SSNs the broker pressed Change on and then left BLANK — refused
 * inline rather than treated as "keep it" (BRK-S316). SSN is required on every
 * roster member; an untouched masked field is the keep, and it never reaches
 * this list because it is not changing.
 */
export function clearedStoredSsn(
  fields: readonly BondRenderField[],
  bag: BondValueBag,
  changing: (name: string) => boolean,
): BondRenderField[] {
  return fields.filter(
    (field) =>
      field.storedSsn !== undefined &&
      changing(field.name) &&
      isBlankPiiValue(bag[field.name]),
  );
}

/** The inline refusal for a cleared stored SSN. */
export const SSN_CLEARED_MESSAGE =
  "SSN is required — enter the new one, or keep the one on file.";

/**
 * What a successful save leaves this session holding: every SSN it CARRIED,
 * keyed by field name. Memory only — it exists so the broker can check what
 * they typed, and a reload forgets it (NN #9).
 */
export function heldSsnFrom(
  fields: readonly BondRenderField[],
  bag: BondValueBag,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fields) {
    if (contractPathOf(field.name) !== BOND_SSN_PATH) continue;
    const value = bag[field.name];
    if (typeof value === "string" && !isBlankPiiValue(value)) {
      out[field.name] = value;
    }
  }
  return out;
}
